import { Button, CloseButton, SaveButton } from '@ui/shared/Button';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { closePopup, openPopup } from '../../../data/providers/popupsProvider';
import {
  ITestsSetting,
  testsSettingProvider,
} from '../../../data/providers/settings/testsSettingProvider';
import { ISelectableTestType } from '../../../data/types';
import { saveTestsSetting } from '../../../data/writers/settings/testsSetting';
import { CardBody } from '../../shared/card/CardBody';
import { CardFooter } from '../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../shared/card/CardHeader';
import { ControlSpacer } from '../../shared/form/ControlSpacer';
import { FormContainer } from '../../shared/form/FormContainer';
import { TestTypeSelect } from '../../shared/form/selects/TestTypeSelect';
import { LinearLoading } from '../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../shared/popups/PopupCard';

interface IPopupProps {
  onClose: () => void;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [facet, setFacet] = React.useState<string | null>();
  const [testType, setTestType] = React.useState<ISelectableTestType>();

  /**
   * @description subscribe to test setting
   */
  React.useEffect((): (() => void) => {
    const subscriber = testsSettingProvider().subscribe(
      (setting: ITestsSetting): void => {
        setLoading(false);
        setFacet(setting.facet);
      }
    );
    return (): void => subscriber.unsubscribe();
  }, []);

  React.useEffect((): void => {
    setFacet(testType?.value);
  }, [testType]);

  /**
   * @description persists setting to the database
   */
  const saveHandler = async (): Promise<void> => {
    setIsSaving(true);
    await saveTestsSetting({ facet });
    setIsSaving(false);
    props.onClose();
  };

  /**
   * @description persists setting to the database
   */
  const clearHandler = async (): Promise<void> => {
    setIsSaving(true);
    setFacet(null);
    await saveTestsSetting({ facet: null });
    setIsSaving(false);
    props.onClose();
  };

  const changeHandler = (
    value: ISelectableTestType | ISelectableTestType[]
  ): void => setTestType(value as ISelectableTestType);

  return (
    <PopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft style={{ marginLeft: '10px' }}>
            <h6>Choose Test Type</h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {(loading || isSaving) && <LinearLoading />}
          <FormContainer style={{ alignItems: 'flex-start' }}>
            <ControlSpacer style={{ width: '100%' }}>
              <PrimaryHoverSelect>
                <TestTypeSelect
                  onChange={changeHandler}
                  value={testType as ISelectableTestType}
                  disabled={false}
                />
              </PrimaryHoverSelect>
            </ControlSpacer>
          </FormContainer>
        </CardBody>
        <CardFooter surface={8}>
          <CardHeaderRight>
            <Button title='Cancel' icon='cancel' onClick={clearHandler} disabled={isSaving} />
            <SaveButton
              type='submit'
              onClick={saveHandler}
              disabled={isSaving}
            />
          </CardHeaderRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

export const TestTypePopup = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const open = (): void => {
      openPopup(
        <Popup onClose={closePopup(props.history)} />,
        props.history,
        'test-type'
      );
    };
    return (
      <Button onClick={open} title='Test Type' icon='type_specimen' />
    );
  }
);

const PrimaryHoverSelect = styled.div`
  .react-select__option--is-focused {
    background-color: var(--color-primary);
  }
`;
